/**
 * All portfolio copy and media manifests live here so the section components
 * stay layout-only. Clip ids map to `/media/<id>.webm` plus a matching
 * `/media/<id>.jpg` poster, both produced by scripts/build-media.sh.
 */

import itsec from './static/itsec.png';
import diagramTexture from './static/diagram-1.png';
import diagramPipeline from './static/diagram-2.png';
import vrScene from './static/vr.png';
import breathRig1 from './static/fp_breath_1.jpg';
import breathRig2 from './static/fp_breath_2.jpg';
import breathRig3 from './static/fp_breath_3.jpg';

/** Shape of the box a clip or still is cropped into. */
export type Ratio = '16/9' | '1/1' | 'natural';

export interface Clip {
  /** Basename in /public/media, without extension. */
  id: string;
  caption: string;
  /** Defaults to 16/9; set where the encoded source is not widescreen. */
  ratio?: Ratio;
}

export interface Still {
  src: string;
  alt: string;
  caption?: string;
  ratio?: Ratio;
}

/** A full-bleed photo band with copy over it, for a credential that deserves
 *  more weight than a thumbnail and a caption. */
export interface Band {
  src: string;
  alt: string;
  eyebrow: string;
  title: string;
  text: string;
}

export interface MetaEntry {
  label: string;
  value: string;
}

export interface CaseStudy {
  id: string;
  index: string;
  title: string;
  subtitle: string;
  meta: MetaEntry[];
  lede: string;
  body: string[];
  features?: { heading: string; items: string[] };
  feature: Clip;
  clips: Clip[];
  band?: Band;
  stills?: Still[];
  diagrams?: Still[];
}

export interface Gallery {
  id: string;
  title: string;
  blurb: string;
  clips: Clip[];
  square?: boolean;
  still?: Still;
}

/* ─── Hero ─────────────────────────────────────────────────── */

export const HERO = {
  name: 'Jacob Noel',
  role: 'Real-time graphics & interaction engineer',
  lede:
    'Simulators, VR systems, and shader work in Unity and C#, often wired to hardware I ' +
    'built. Demoed to U.S. Army Cyber Command, exhibited at I/ITSEC, built for a NASA ' +
    'contractor.',
  location: 'Oviedo, Florida',
  clip: 'hero',
  skills: [
    'Unity',
    'C#',
    'HLSL / GLSL',
    'Unreal Engine',
    'XR Interaction Toolkit',
    'Arduino',
    'C++',
    'Python',
  ],
};

/* ─── Selected work ────────────────────────────────────────── */

export const CASE_STUDIES: CaseStudy[] = [
  {
    id: 'electronic-enforcers',
    index: '01',
    title: 'Electronic Enforcers',
    subtitle: 'A real-time electronic warfare training simulator',
    meta: [
      { label: 'Role', value: 'Technical lead, original designer; later CTO, Zuleris Interactive' },
      { label: 'Stack', value: 'Unity · C# · HLSL · Python (NumPy, SciPy)' },
      { label: 'Timeline', value: '2023 – 2025' },
      { label: 'Shipped', value: 'Exhibited at I/ITSEC; demoed to U.S. Army Cyber Command' },
    ],
    lede:
      'A field spectrum-analysis trainer rendering a live RF environment you can walk ' +
      'through. I designed it and led it from senior design into a funded product.',
    body: [
      'A spectrum analyzer redraws a full sweep every frame, and what you see depends on ' +
        'where you stand: which emitters are in range, what sits between, how much the ' +
        'environment takes out of each band.',
      'The **signal** is precomputed: spectral content over time plus environmental ' +
        'attenuation, baked into a GPU-samplable texture. Frequency ranges on one axis, time ' +
        'slices on the other, minimum, maximum, real and imaginary packed into the four bytes ' +
        'of a 32-bit texel. NumPy bakes, SciPy modulates.',
      '**Propagation** cannot be precomputed: it depends on receiver position. Solved live ' +
        'with Monte Carlo methods. Each frame the render pass samples the baked array, scales ' +
        'it by the received intensity, takes magnitude, and post-processes, all in compute and ' +
        'fragment shaders.',
      'Coursework to show floor to paid work: Zuleris Interactive took it on as subcontractor ' +
        'to Chesapeake Technology International. I was CTO at Zuleris and coordinated ' +
        'development.',
    ],
    features: {
      heading: 'What I built',
      items: [
        'Signal baking pipeline: spectral content and attenuation precomputed into GPU textures. NumPy bakes, SciPy modulates',
        'Real-time Monte Carlo propagation, solved per frame against receiver position',
        'Compute and fragment shader chain sampling the baked signal, scaled by live propagation',
        'Instructor debug mode rendering invisible emitters as light sources',
        'Minimap with axes and labels correct through zoom and pan',
        'Horizontal and radial compass: cardinals, degrees, mils',
        'Recording and playback with scrubbing, serialization, and export/import',
        'ECEF, ENU, and geodetic conversion against a terrain reference',
        'DTED-accurate terrain generation',
        'Several spectrogram display modes',
      ],
    },
    feature: { id: 'ee-signal', caption: 'Signal visualization: emitters as light sources for instructors' },
    clips: [
      { id: 'ee-minimap', caption: 'Minimap under zoom and translation' },
      { id: 'ee-playback', caption: 'Session recording and playback' },
      { id: 'ee-spectrogram', caption: 'Spectrogram display modes' },
    ],
    band: {
      src: itsec,
      alt: 'I/ITSEC exhibitor badge issued to Jacob Noel, Zuleris Interactive LLC',
      eyebrow: 'Exhibited',
      title: 'I/ITSEC 2023',
      text:
        'Demoed to Lt. Gen. Maria Barrett and Steven Rehn of U.S. Army Cyber Command, and at ' +
        'the Ash Carter Exchange for NSIN.',
    },
    diagrams: [
      { src: diagramTexture, alt: 'Diagram of the signal texture format, packing spectrum components into RGBA channels', caption: 'Signal texture format' },
      { src: diagramPipeline, alt: 'Diagram showing the baked signal texture array sampled per frame and scaled by received intensity before rendering', caption: 'Baked signal sampled per frame, scaled by live received intensity' },
    ],
  },

  {
    id: 'breath-input',
    index: '02',
    title: 'Breath Input Module',
    subtitle: 'Non-contact breath sensing as a VR input device',
    meta: [
      { label: 'Role', value: 'Sole developer, hardware and software' },
      { label: 'Stack', value: 'Unity · C# · XR Interaction Toolkit · Arduino · Bluetooth · custom sensor' },
      { label: 'Timeline', value: 'Spring 2025' },
      { label: 'Context', value: 'Advanced Virtual Reality, UCF' },
    ],
    lede:
      'A custom airflow sensor on the headset, sampled by an Arduino and streamed into Unity ' +
      'as a continuous directional input axis.',
    body: [
      'The sensor reads directional airflow without contact, so the raw signal is noisy and ' +
        'drifty, nothing like a button press. The work was on both ends: conditioning the ' +
        'sensor, then mapping an analog stream onto interactions that **feel** responsive ' +
        'rather than merely register.',
      'It started as a wired serial link, then moved to Bluetooth so it could run against a ' +
        'standalone Quest instead of a tethered PC.',
      'The companion scene makes the input legible: candle flames that bend, chimes that ' +
        'swing, a vector field you can push. Each is a shader or particle system authored so a ' +
        'soft continuous input reads as cause and effect.',
    ],
    feature: { id: 'breath-field', caption: 'Breath driving an interactive vector field' },
    clips: [
      { id: 'breath-input', caption: 'Sensor output driving the input axis', ratio: '1/1' },
      { id: 'breath-interaction', caption: 'Interacting with the scene by breath', ratio: '1/1' },
    ],
    stills: [
      { src: breathRig1, alt: 'Breath sensor wired to a VR headset, resting on a floor' },
      { src: breathRig2, alt: 'Close view of the sensor mounted at the headset facial interface' },
      { src: breathRig3, alt: 'The assembled sensor rig held in hand' },
    ],
  },

  {
    id: 'lunar-dust',
    index: '03',
    title: 'Lunar Dust Simulation',
    subtitle: 'Nanite-based regolith surface for a NASA contractor',
    meta: [
      { label: 'Role', value: 'Graduate research assistant, UCF VARLAB' },
      { label: 'Stack', value: 'Unreal Engine · Nanite' },
      { label: 'Timeline', value: 'Aug 2024 – Jan 2026' },
      { label: 'Context', value: 'Built for a NASA contractor; physical parameters validated with Exolith Lab' },
    ],
    lede:
      'Real-time simulation of the lunar surface: how regolith deforms and settles when ' +
      'disturbed.',
    body: [
      'My part was the surface. Lunar regolith does not settle like terrestrial dust, and ' +
        'reproducing that at interactive frame rates meant Nanite-based methods. The gas and ' +
        'fluid side is Niagara, and sat with others.',
      'Much of the role was not rendering but translation: intermediary between Exolith Lab ' +
        'and other departments, turning their measurements into parameters the simulation ' +
        'could honor.',
    ],
    feature: { id: 'dust-surface', caption: 'Surface disturbance and settling' },
    clips: [
      { id: 'dust-accumulation', caption: 'Dust accumulating on the surface' },
    ],
  },
];

/* ─── Galleries ────────────────────────────────────────────── */

export const GALLERIES: Gallery[] = [
  {
    id: 'interaction',
    title: 'VR & Interaction',
    blurb:
      'VR coursework set pieces, each isolating one problem: how an interaction should feel, ' +
      'how a menu behaves when you are not looking at it, how to place a sun with a controller.',
    square: true,
    still: {
      src: vrScene,
      alt: 'A warmly lit interior VR scene with baked lighting',
      caption: 'Scene design: bloom, baked lighting, emissive materials',
    },
    clips: [
      { id: 'vr-glove', caption: 'Gravity-glove pull: objects on a guided arc to the hand' },
      { id: 'vr-environment', caption: 'Angle-faded menu: opacity varies with viewing angle' },
      { id: 'vr-sun', caption: 'In-scene sun placement, bi-colored position indicator' },
      { id: 'vr-softbody', caption: 'Soft-body interaction and pump mechanics' },
    ],
  },
  {
    id: 'graphics',
    title: 'Real-Time Graphics',
    blurb:
      'Shader and GPU work, mostly for its own sake: particles, splines, procedural meshes, ' +
      'volumetrics. The part of the job I would do anyway.',
    clips: [
      { id: 'gfx-water', caption: 'Ray-traced real-time water shading' },
      { id: 'gfx-volumetric', caption: 'Volumetric normal authoring' },
      { id: 'gfx-iridescence', caption: 'Thin-film iridescence' },
      { id: 'gfx-particles', caption: 'Particle and spline effects' },
      { id: 'gfx-vectorfield', caption: 'Interactive vector field' },
    ],
  },
];

/* ─── Background ───────────────────────────────────────────── */

export const EXPERIENCE = [
  {
    org: 'UCF Virtual and Augmented Reality Lab',
    role: 'Graduate Research Assistant',
    period: 'Aug 2024 – Jan 2026',
    note: 'Nanite regolith surface for a NASA contractor; liaison to Exolith Lab on physical accuracy.',
  },
  {
    org: 'Zuleris Interactive LLC',
    role: 'CTO & Software Engineer',
    period: 'Aug 2024 – Aug 2025',
    note:
      'Led the Electronic Enforcers simulator as subcontractor to Chesapeake Technology ' +
      'International. Emphasis on TAKX integration.',
  },
  {
    org: 'National Security Innovation Network',
    role: 'X-Force Fellow',
    period: 'Jun 2024 – Aug 2024',
    note: 'Fellowship continuing the real-time spectrum analysis simulation.',
  },
  {
    org: 'UCF Department of Mathematics',
    role: 'Teaching Assistant, Discrete Mathematics',
    period: 'Two semesters',
    note: 'Lectured labs, held office hours, proctored and graded. Awarded by the Physics Department for physics sequence performance.',
  },
];

export const WRITING = [
  {
    title: 'Full symmetric Tutte monodromy: hypercubes, uniform matroids, and density criteria',
    venue: 'Zenodo preprint',
    period: 'September 2026',
    doi: '10.5281/zenodo.22857279',
    href: 'https://zenodo.org/records/22857279',
    note:
      'Proves the Bohn\u2013Cameron\u2013M\u00fcller conjecture for hypercube graphs and uniform ' +
      'matroids, with a density criterion covering arc-transitive graphs. Sole author, outside ' +
      'coursework.',
  },
];

export const EDUCATION = [
  { school: 'University of Central Florida', award: 'M.S. Computer Science', period: 'August 2026' },
  { school: 'University of Central Florida', award: 'B.S. Computer Science & B.S. Mathematics', period: 'Aug 2024' },
  { school: 'Seminole State College', award: 'A.A. General', period: 'Dec 2022' },
];

export const SKILLS = [
  { group: 'Languages', items: 'C#, C/C++, Python, GLSL/HLSL, Java, LaTeX' },
  { group: 'Engines & tools', items: 'Unity, Unreal Engine, Android, Mathematica, Linux, Git' },
  { group: 'Real-time & XR', items: 'XR Interaction Toolkit, OpenGL, GPU parallelism, shader authoring, embedded I/O, Bluetooth' },
  { group: 'CS & math', items: 'Software architecture, formal verification, graph theory, higher algebra' },
];

export const CONTACT = {
  email: 'contact@jacob-noel.com',
  phone: '561-516-2390',
  phoneHref: 'tel:+15615162390',
  location: 'Oviedo, Florida. Open to on-site work',
  resume: process.env.PUBLIC_URL + '/Resume.pdf',
};
